import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { DeviceSettingsService } from '../../library/device-settings.service';
import { WebRTCDiagnosticService } from 'src/app/services/general/webrtc-diagnostic.service';
import { IDiagnosticResult } from 'src/app/interfaces/idiagnostic-result';
import { Subscription } from 'rxjs';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { LoggerService } from 'src/app/services/logger/logger.service';

@Component({
  selector: 'app-device-settings-diagnostic',
  templateUrl: './device-settings-diagnostic.component.html',
  styleUrls: ['./device-settings-diagnostic.component.scss'],
  standalone: false,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'settings',
    },
  ],
})
export class DeviceSettingsDiagnosticComponent implements OnInit, OnDestroy {
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Output() diagnosticRunningChange = new EventEmitter<boolean>();
  @ViewChild('floatingRef', { static: false }) floatingRef?: ElementRef;

  isRunningDiagnostic: boolean = false;
  isDiagnosticComplete: boolean = false;
  diagnosticResult: IDiagnosticResult | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private rtcDiagnosticService: WebRTCDiagnosticService,
    private translationService: TranslationService,
    private logger: LoggerService
  ) { }

  ngOnInit(): void {
    this.resetDiagnosticState();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.resetDiagnosticState();
  }

  private resetDiagnosticState(): void {
    this.isRunningDiagnostic = false;
    this.isDiagnosticComplete = false;
    this.diagnosticResult = null;
    this.diagnosticRunningChange.emit(false);
    this.logger.log('[CALL] Diagnostic State - Cleaned');
  }

  async runDiagnostic(auto = false) {
    // auto param can be used when we open the panel due to Assembly error
    if (this.isRunningDiagnostic) return;
    this.isRunningDiagnostic = true;
    this.diagnosticRunningChange.emit(true);
    try {
      this.diagnosticResult = await this.rtcDiagnosticService.runDiagnostics();
      this.logger.log('[CALL] Diagnostic Result', this.diagnosticResult);
      this.isRunningDiagnostic = false;
    } catch (err) {
      this.isDiagnosticComplete = true;
      this.isRunningDiagnostic = false;
      this.diagnosticRunningChange.emit(false);
      this.diagnosticResult = {
        summary: this.translationService.translate('DIAGNOSTIC_FAILED', 'settings'),
        timestamp: new Date().toISOString(),
        raw: { error: String(err) },
        hasPassed: false,
      } as any;
      this.logger.error('[CALL] Diagnostic Result', err, this.diagnosticResult || {});
    } finally {
      this.isRunningDiagnostic = false;
      this.isDiagnosticComplete = true;
      this.diagnosticRunningChange.emit(false);
    }
  }

  async onRunDiagnosticClick() {
    if (this.diagnosticResult) {
      // If there's already a result, reset state first
      this.resetDiagnosticState();
    }
    await this.runDiagnostic();
  }

  onCopyResultsClick() {
    this.copyResultsToClipboard();
  }

  copyResultsToClipboard() {
    if (!this.diagnosticResult) return;
    const payload = this.formatPayloadForSupport(this.diagnosticResult);
    navigator.clipboard.writeText(payload);
  }

  private formatPayloadForSupport(res: IDiagnosticResult) {
    // Friendly JSON + short header
    const header = `${this.translationService.translate('DIAGNOSTIC_QUANTIFIED_DIAGNOSTICS', 'settings')}\n${this.translationService.translate('DIAGNOSTIC_TIME', 'settings')}: ${res.timestamp}\n${this.translationService.translate('DIAGNOSTIC_SUMMARY', 'settings')}: ${res.summary}\n\n`;
    const raw = JSON.stringify(res.raw, null, 2);
    const meta = `\n\n${this.translationService.translate('DIAGNOSTIC_CLIENT_INFO', 'settings')}:\n- UA: ${navigator.userAgent}\n- ${this.translationService.translate('DIAGNOSTIC_PLATFORM', 'settings')}: ${navigator.platform}\n`;
    return header + raw + meta;
  }

}

